const THEME_STORAGE_KEY = 'student-portal-theme'

const palette = {
  blue: {
    50: 'var(--color-blue-50, #537ab7)',
    100: 'var(--color-blue-100, #316bf4)',
    200: 'var(--color-blue-200, #4788FF)',
    250: '#4274F6',
    300: 'var(--color-blue-300, #1E4397)'
  },
  navy: {
    500: 'var(--color-navy-500, #2F468E)',
    700: 'var(--color-navy-700, #1F2F6D)'
  },
  purple: {
    500: 'var(--color-purple-500, #9080FF)'
  },
  yellow: {
    500: 'var(--color-yellow-500, #FED100)'
  },
  amber: {
    700: 'var(--color-amber-700, #9A7400)'
  },
  gray: {
    50: 'var(--color-gray-50, #FAFAFA)',
    100: 'var(--color-gray-100, #f9fafb)',
    200: 'var(--color-gray-200, #F5F5F5)',
    300: 'var(--color-gray-300, #e7e7e7)',
    350: 'var(--color-gray-350, #E3E5E9)',
    400: 'var(--color-gray-400, #DEDEDE)',
    500: 'var(--color-gray-500, #CCCCCC)',
    550: 'var(--color-gray-550, #B6B6B6)',
    600: 'var(--color-gray-600, #707070)',
    650: 'var(--color-gray-650, #4E4E4E)',
    700: 'var(--color-gray-700, #515151)',
    725: 'var(--color-gray-725, #545454)',
    750: 'var(--color-gray-750, #454545)',
    800: 'var(--color-gray-800, #323233)',
    900: 'var(--color-gray-900, #2B2B2B)',
    1000: 'var(--color-gray-1000, #262728)',
    1100: 'var(--color-gray-1100, #858585)'
  },
  green: {
    500: 'var(--color-green-500, #208268)',
    600: 'var(--color-green-600, #49B0B9)',
    700: 'var(--color-green-700, #1E8F39)',
    800: 'var(--color-green-800, #1B6E2C)',
    900: 'var(--reports-proficiency, #1F8F3A)'
  },
  red: {
    400: 'var(--color-red-400, #C44538)',
    500: 'var(--color-red-500, #D0021B)',
    600: 'var(--reports-critical, #B9382A)',
    700: 'var(--color-red-700, #962D22)'
  }
}

const lightTheme = {
  icons: {
    stroke: palette.gray[800],
    correct: palette.green[700],
    incorrect: palette.red[400]
  },
  brand: {
    yellow: palette.yellow[500],
    primaryBlue: palette.blue[200],
    proficientGreen: palette.green[900],
    criticalRed: palette.red[600]
  },
  surface: {
    error: palette.red[500],
    info: palette.blue[100],
    infoLight: palette.blue[50],
    warn: palette.yellow[500],
    hover: palette.gray[100],
    success: palette.green[500],
    muted: palette.gray[300],
    infoDark: palette.blue[300],
    errorDark: palette.red[400]
  },
  text: {
    primary: palette.gray[800],
    paragraph: palette.gray[600],
    success: palette.green[500],
    highlight: palette.purple[500],
    clever: palette.blue[250],
    info: palette.blue[100],
    legend: palette.gray[700],
    infoLight: palette.blue[50],
    error: palette.red[500],
    barLabel: palette.gray[750]
  },
  border: {
    primary: palette.gray[500],
    secondary: palette.gray[400],
    info: palette.blue[100],
    active: palette.gray[600],
    success: palette.green[500],
    dotted: palette.gray[1100],
    /** OPR / shared print header underline — matches legacy `$opr-print-page-header-border`. */
    reportPrintHeader: 'var(--surface-primary, #194792)'
  },
  button: {
    new: palette.green[600],
    primary: palette.blue[200]
  },
  card: {
    background: '#ffffff',
    secondary: palette.gray[200],
    hover: palette.gray[100],
    surface: palette.gray[50],
    highlight: palette.blue[200]
  },
  skeleton: {
    primary: palette.gray[200],
    secondary: palette.gray[300]
  }
}

const darkTheme = {
  icons: {
    stroke: palette.gray[550],
    correct: palette.green[700],
    incorrect: palette.red[400]
  },
  brand: {
    yellow: palette.yellow[500],
    primaryBlue: palette.blue[200],
    proficientGreen: palette.green[900],
    criticalRed: palette.red[600]
  },
  surface: {
    error: palette.red[500],
    info: palette.blue[100],
    infoLight: palette.blue[50],
    warn: palette.yellow[500],
    hover: palette.gray[700],
    success: palette.green[500],
    muted: palette.gray[700],
    infoDark: palette.blue[300],
    errorDark: palette.red[400]
  },
  text: {
    primary: palette.gray[350],
    paragraph: palette.gray[600],
    highlight: palette.purple[500],
    clever: palette.blue[250],
    success: palette.green[500],
    info: palette.blue[100],
    legend: palette.gray[500],
    infoLight: palette.blue[50],
    error: palette.red[500],
    barLabel: palette.gray[550]
  },
  border: {
    primary: palette.gray[600],
    info: palette.blue[100],
    secondary: palette.gray[400],
    active: palette.gray[500],
    success: palette.green[500],
    dotted: palette.gray[1100],
    reportPrintHeader: 'var(--surface-primary, #194792)'
  },
  button: {
    new: palette.green[600],
    primary: palette.blue[200]
  },
  card: {
    background: palette.gray[1000],
    secondary: palette.gray[200],
    hover: palette.gray[800],
    surface: palette.gray[900],
    highlight: palette.gray[725]
  },
  skeleton: {
    primary: palette.gray[800],
    secondary: palette.gray[1000]
  }
}

const generics = {
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px'
  },
  typography: {
    fontNunito: "'Nunito', sans-serif",
    fontNunitoSans: "'Nunito Sans', sans-serif",
    helveticaNeue: "'Helvetica Neue', sans-serif",
    fontInter: "'Inter', sans-serif",
    fontPlusJakartaSans: "'Plus Jakarta Sans', sans-serif"
  },
  animation: {
    duration: '0.15s',
    style: 'ease'
  }
}

export type ThemeMode = 'light' | 'dark'
export type ContrastMode =
  | 'black_on_white'
  | 'white_on_black'
  | 'dark_blue_on_gold'
  | 'yellow_on_red'
  | 'yellow_on_dark_green'
  | 'light_pink_on_dark_blue'
  | 'black_on_cream'
  | 'dark_blue_on_light_blue'
  | 'yellow_on_dark_blue'
  | 'black_on_yellow'

export const DEFAULT_CONTRAST_MODE: ContrastMode = 'black_on_white'

type ContrastTonePair = {
  primary: string
  secondary: string
}

export type ContrastColors = {
  text: ContrastTonePair
  background: ContrastTonePair
}

export const contrastColorsByMode: Record<ContrastMode, ContrastColors> = {
  black_on_white: {
    text: { primary: '#000000', secondary: '#000000' },
    background: { primary: '#FFFFFF', secondary: '#F6F6F6' }
  },
  white_on_black: {
    text: { primary: '#FFFFFF', secondary: '#EBEBEB' },
    background: { primary: '#000000', secondary: '#121212' }
  },
  dark_blue_on_gold: {
    text: { primary: '#253A63', secondary: '#11264F' },
    background: { primary: '#EDCE5A', secondary: '#FFE06C' }
  },
  yellow_on_red: {
    text: { primary: '#FDFF4B', secondary: '#E9EB37' },
    background: { primary: '#86210F', secondary: '#983321' }
  },
  yellow_on_dark_green: {
    text: { primary: '#FDFF4B', secondary: '#E9EB37' },
    background: { primary: '#244B29', secondary: '#365D3B' }
  },
  light_pink_on_dark_blue: {
    text: { primary: '#FBECEF', secondary: '#E7D8DB' },
    background: { primary: '#2E4554', secondary: '#405766' }
  },
  black_on_cream: {
    text: { primary: '#010101', secondary: '#000000' },
    background: { primary: '#F9F0DE', secondary: '#FFF2F0' }
  },
  dark_blue_on_light_blue: {
    text: { primary: '#040404', secondary: '#000000' },
    background: { primary: '#C4D8F9', secondary: '#D6EAFF' }
  },
  yellow_on_dark_blue: {
    text: { primary: '#E1DF9C', secondary: '#CDCB88' },
    background: { primary: '#1A2738', secondary: '#2C394A' }
  },
  black_on_yellow: {
    text: { primary: '#1A2738', secondary: '#061324' },
    background: { primary: '#E1DF9C', secondary: '#F3F1AE' }
  }
}

export const getContrastColors = (mode: ContrastMode) => {
  return (
    contrastColorsByMode[mode] || contrastColorsByMode[DEFAULT_CONTRAST_MODE]
  )
}
export type ThemePalette = typeof lightTheme
export type AppTheme = {
  colors: ThemePalette
  animation: typeof generics.animation
  typography: typeof generics.typography
  breakpoints: typeof generics.breakpoints
  mode: ThemeMode
  contrast: {
    id: ContrastMode
    text: ContrastTonePair
    background: ContrastTonePair
  }
}

const resolveThemeMode = (value: string | null): ThemeMode =>
  value === 'dark' ? 'dark' : 'light'

export const getStoredThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light'
  return resolveThemeMode(localStorage.getItem(THEME_STORAGE_KEY))
}

export const setStoredThemeMode = (mode: ThemeMode) => {
  if (typeof window === 'undefined') return
  localStorage.setItem(THEME_STORAGE_KEY, mode)
}

export const getThemepalette = (
  mode: ThemeMode = getStoredThemeMode()
): AppTheme => {
  const defaultContrast = getContrastColors(DEFAULT_CONTRAST_MODE)

  return {
    colors: mode === 'dark' ? darkTheme : lightTheme,
    animation: generics.animation,
    breakpoints: generics.breakpoints,
    typography: generics.typography,
    mode,
    contrast: {
      id: DEFAULT_CONTRAST_MODE,
      text: defaultContrast.text,
      background: defaultContrast.background
    }
  }
}

// Backward-compatible exports for existing imports.
export const theme = getThemepalette()
export const colors: ThemePalette = theme.colors
